import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ILegalEntitiesBusinessesResponse } from '../../models/shared.model';
import { LegalEntityHistoryService } from '../../legal-entity-history.service';
import { Store } from '@ngrx/store';
import * as toDoActions from '../../../features/todos/actions';
import * as authActions from '../../../features/auth/actions';
import * as fromActions from '../../../features/legal-entities/actions';
import * as fromToDoActions from '../../../features/todos/actions';
import { fromGetBusinessDetailsActions, clearGetProofBusinessFicaDocs } from '../../../features/businesses-detail/actions';
import { SharedService } from '../../shared.service';
import * as fromAllUsersActions from '../../../features/legal-entities/actions/all-users.actions';
import { IBusinessUserRolesRequest } from '../../../features/auth/models/business-user-roles.model';
import { PermissionService } from '../../../core/services/permission.service';
import { EMPTY_GUID } from '../../constants';
import { RouteURL, SessionStorageIds } from '../../enums';
import { AdviserLandingService } from '../../../core/services/adviser-landing.service';

@Component({
  selector: 'app-legal-entity-history',
  templateUrl: './legal-entity-history.component.html',
  styleUrls: ['./legal-entity-history.component.css']
})
export class LegalEntityHistoryComponent implements OnInit {
  historyItems: ILegalEntitiesBusinessesResponse[] = [];
  @Output() closePopupEvent = new EventEmitter<void>();
  @Output() entitySelectedEvent = new EventEmitter<void>();

  constructor(
    private legalEntityHistoryService: LegalEntityHistoryService,
    private store: Store<any>,
    private sharedService: SharedService,
    private permissionService: PermissionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  loadHistory(): void {
    this.historyItems = this.legalEntityHistoryService.getHistory();
  }

  selectLegalEntity(entity: ILegalEntitiesBusinessesResponse): void {
    // Save to history (moves to top)
    this.legalEntityHistoryService.addToHistory(entity);

    // Load the legal entity (same logic as search-legal-entities component)
    sessionStorage.setItem('IsFromToDo', 'false');
    this.sharedService.isFromLegalEntities = true;

    // Set sessionStorage before store dispatches so side nav and other subscribers see the new entity when they react to the store
    sessionStorage.setItem('businessId', entity.legalEntityId);
    sessionStorage.setItem('legalEntity', JSON.stringify(entity));
    this.updatePermissionsForLegalEntity(entity.legalEntityId);

    this.store.dispatch(toDoActions.loadRegulationVerified({ businessId: entity.legalEntityId }));
    this.store.dispatch(fromActions.clearBusinessSelected());
    this.store.dispatch(fromActions.clearAssetManagerViewLinkedBusinesses());
    this.store.dispatch(fromActions.loadClearManageOfferings());
    this.store.dispatch(
      fromActions.saveSelectedBusinessId({
        save: entity.legalEntityId
      })
    );
    this.store.dispatch(
      fromToDoActions.loadSelectedLegalEntity({
        selectedLE: entity
      })
    );

    this.store.dispatch(fromGetBusinessDetailsActions.clearGetBusinessDetailsResponse());
    this.store.dispatch(clearGetProofBusinessFicaDocs());

    const userRolesReq: IBusinessUserRolesRequest = {
      businessId: entity.legalEntityId,
      token: sessionStorage.getItem('token') || '',
      uniqueDeviceId: EMPTY_GUID
    };
    this.store.dispatch(authActions.loadBusinessUserRoles({ request: userRolesReq }));

    this.store.dispatch(
      fromAllUsersActions.getBusiness({
        businessId: entity.legalEntityId
      })
    );

    const currentUrl = this.router.url;
    const isMemberOrProductDetail =
      currentUrl.indexOf('/employee/employee-details') !== -1 ||
      currentUrl.indexOf('/product-detail') !== -1;

    // Defer close and optional navigation to next tick so store/session updates are applied first.
    // Fixes first selection not working (UI not updating until second selection).
    setTimeout(() => {
      this.entitySelectedEvent.emit();
      this.closePopup();
      if (AdviserLandingService.isPayrollOnly()) {
        // William Strike users only have Upload Payroll; the page follows the new legal entity
        this.router.navigateByUrl(RouteURL.WS_PAYROLL_UPLOAD);
      } else if (isMemberOrProductDetail) {
        this.router.navigate([RouteURL.LEGAL_ENTITY_DASHBOARD]);
      }
    }, 0);
  }

  private updatePermissionsForLegalEntity(legalEntityId: string): void {
    const stored = sessionStorage.getItem(SessionStorageIds.AUTH_LEGAL_ENTITY_PERMISSIONS);
    const legalEntityPermissions = stored ? (JSON.parse(stored) as any[]) : [];
    const current = legalEntityPermissions.find(entry => entry.legalEntityId === legalEntityId);
    sessionStorage.setItem(SessionStorageIds.AUTH_PERMISSIONS, JSON.stringify(current?.permissions || []));
    sessionStorage.setItem(SessionStorageIds.AUTH_CURRENT_LEGAL_ENTITY_ID, legalEntityId);
    this.permissionService.refreshFromSession();
  }

  closePopup(): void {
    this.closePopupEvent.emit();
  }
}
